import queryString from "query-string";

import { setLocalStorage, getLocalStorage } from "js/helpers/functions";
import {
    COLOR_PICKER_RECENT_PALETTE,
    ICON_EDITOR_PREVIEW_SHAPE
} from "core/localStorageKeys";

import { normalizeHexCode, isHexCode } from "icons/IconEditor/helpers";
import { SHAPES } from "icons/IconEditor/constants";

import colors from "core/colors";
import { iconEditorPreviewEndpoint, iconEditorTokenEndpoint } from "data/links";
import {
    iconEditorInitialState,
    iconTransformsInitialState
} from "js/reducers/icon-editor/state";

/* -------------------------------------------------------------------------- */
/*                               PERSISTED DATA                               */
/* -------------------------------------------------------------------------- */

// Strip out bad color values that could break the editor / page
const validateColors = (colors, allowTransparent = false) => {
    if (!Array.isArray(colors)) {
        return colors;
    }
    return colors.filter(color => {
        if (allowTransparent && color === "") {
            return true;
        }
        return isHexCode(color); // Filter out non-hex values
    });
};

// Strip out bad shape values that could break the editor / page
const validateShape = shape =>
    Object.values(SHAPES).includes(shape) ? shape : null;

export const getPersistedRecentColorsPalette = () => {
    let {
        iconColorsRecent: iconColors = null,
        backgroundColorsRecent: backgroundColors = null
    } = getLocalStorage(COLOR_PICKER_RECENT_PALETTE) || {};

    const persistedIconsColors = validateColors(iconColors);
    const persistedBackgroundColors = validateColors(backgroundColors, true);

    return {
        persistedIconsColors,
        persistedBackgroundColors
    };
};

export const getPersistedShape = () => {
    let shape = getLocalStorage(ICON_EDITOR_PREVIEW_SHAPE);
    shape = validateShape(shape);
    return shape;
};

export const persistUpdatedRecentColorsPalettes = ({
    updatedRecentIconColors = [],
    updatedRecentBackgroundColors = []
}) =>
    setLocalStorage(COLOR_PICKER_RECENT_PALETTE, {
        iconColorsRecent: [...updatedRecentIconColors],
        backgroundColorsRecent: [...updatedRecentBackgroundColors]
    });

export const persistUpdatedShape = ({ shape }) => {
    const currentPersistedShape = getLocalStorage(ICON_EDITOR_PREVIEW_SHAPE);

    if (currentPersistedShape !== shape) {
        setLocalStorage(ICON_EDITOR_PREVIEW_SHAPE, shape);
    }
};

/* -------------------------------------------------------------------------- */
/*                               EDITOR PREVIEW                               */
/* -------------------------------------------------------------------------- */

/* ---------------------------------- TOKEN --------------------------------- */

export const fetchToken = async () => {
    const response = await fetch(
        new URL(iconEditorTokenEndpoint, window.location.origin)
    );
    return response;
};

/* ------------------------------ PREVIEW IMAGE ----------------------------- */

export const fetchEditorPreviewImage = async ({ state, iconId }) => {
    const requestQueryString = generateQueryString({
        state,
        iconId: state.currentIconId || iconId
    });

    const requestUrl = `${iconEditorPreviewEndpoint({
        iconId: state.currentIconId
    })}?${requestQueryString}`;

    const editorPreviewResponse = await fetch(
        new URL(requestUrl, window.location.origin)
    );

    return editorPreviewResponse;
};

export const getPreviewImage = async ({ state, dispatch }) => {
    if (state.token) {
        const response = await fetchEditorPreviewImage({
            state
        });
        if (!response.ok) {
            dispatch({
                type: "FETCH_PREVIEW_IMAGE_FAILURE",
                tokenIsExpired: response.status === 401
            });

            return;
        }

        dispatch({
            type: "FETCH_PREVIEW_IMAGE_SUCCESS",
            previewImageHtml: await response.text()
        });
    } else {
        dispatch({
            type: "FETCH_PREVIEW_IMAGE_FAILURE",
            tokenIsExpired: false
        });

        dispatch({
            type: "FETCH_PREVIEW_IMAGE_TOKEN"
        });
    }
};

const generateQueryString = ({ state, iconId }) => {
    const activeBackgroundColor = state.backgroundColors.activeColor;

    return queryString.stringify({
        foregroundColor: normalizeHexCode({
            hexCode: state.iconColors.activeColor
        }),
        foregroundOpacity: 1.0,
        backgroundShape: state.shape,
        backgroundShapeOpacity: activeBackgroundColor ? 1.0 : 0,
        backgroundShapeColor: activeBackgroundColor
            ? normalizeHexCode({ hexCode: activeBackgroundColor })
            : normalizeHexCode({ hexCode: colors.secondary.onyx }),
        rotation: state[iconId].rotation,
        flipX: state[iconId].flipX,
        flipY: state[iconId].flipY,
        token: state.token,
        iconFormat: "png",
        exportSize: 752
    });
};

/* -------------------------------------------------------------------------- */
/*                                MISCELLANEOUS                               */
/* -------------------------------------------------------------------------- */

export const iconIsEdited = ({ state, iconId }) => {
    if (!state) return false;

    let iconIsEdited = false;

    let current = state;
    let unedited = iconEditorInitialState;

    if (
        normalizeHexCode({ hexCode: current.iconColors.activeColor }) !==
        normalizeHexCode({ hexCode: unedited.iconColors.activeColor })
    ) {
        iconIsEdited = true;
    }

    if (normalizeHexCode({ hexCode: current.backgroundColors.activeColor })) {
        iconIsEdited = true;
    }

    if (current.shape !== unedited.shape) {
        iconIsEdited = true;
    }

    current = state[iconId];
    unedited = iconTransformsInitialState;

    if (current) {
        if (current.flipX !== unedited.flipX) {
            iconIsEdited = true;
        }

        if (current.flipY !== unedited.flipY) {
            iconIsEdited = true;
        }

        if (current.rotation !== unedited.rotation) {
            iconIsEdited = true;
        }
    }

    return iconIsEdited;
};
